import React, { useEffect, forwardRef, useImperativeHandle } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import './UserAddressForm.scss'

const validationSchema = Yup.object().shape({
    deliveryDate: Yup.string()
        .required('Delivery Date is required'),
    consignee: Yup.string()
        .required('Consignee is required')
        .trim()
        .min(1, 'Consignee is required'),
    addressLabel: Yup.string()
        .required('Address Label is required')
        .trim()
        .min(1, 'Address Label is required'),
    address: Yup.string()
        .required('Address is required')
        .trim()
        .min(3, 'Address must be at least 3 characters'),
    postalCode: Yup.string()
        .required('Postal Code is required')
        .trim()
        .min(3, 'Postal Code must be at least 3 characters'),
    country: Yup.string()
        .required('Country is required'),
    phone: Yup.string()
        .required('Phone Number is required')
        .trim()
        .min(8, 'Phone number must be at least 8 characters'),
    isDefault: Yup.boolean().default(false),
})

const UserAddressForm = forwardRef(({ onFormChange, showIsDefault = true }, ref) => {
    const {
        register,
        watch,
        trigger,
        formState: { errors },
    } = useForm({
        mode: 'onChange',
        resolver: yupResolver(validationSchema),
        defaultValues: {
            deliveryDate: '',
            consignee: '',
            addressLabel: '',
            address: '',
            postalCode: '',
            country: 'Singapore',
            phone: '',
            isDefault: false,
        },
    })

    const formValues = watch()

    useEffect(() => {
        if (onFormChange) {
            onFormChange(formValues)
        }
    }, [
        formValues.deliveryDate,
        formValues.consignee,
        formValues.addressLabel,
        formValues.address,
        formValues.postalCode,
        formValues.country,
        formValues.phone,
        formValues.isDefault,
    ])

    useImperativeHandle(ref, () => ({
        validate: async () => {
            const result = await trigger()
            return result
        },
    }))

    return (
        <div className='user-address-form'>
            <div className='user-address-form__field'>
                <label className='user-address-form__label'>Delivery Date <span className='user-address-form__required'>*</span></label>
                <input
                    type='date'
                    className={`user-address-form__input${errors.deliveryDate ? ' user-address-form__input--error' : ''}`}
                    {...register('deliveryDate')}
                />
                {errors.deliveryDate && <span className='user-address-form__error'>{errors.deliveryDate.message}</span>}
            </div>

            <div className='user-address-form__field'>
                <label className='user-address-form__label'>Consignee <span className='user-address-form__required'>*</span></label>
                <input
                    type='text'
                    className={`user-address-form__input${errors.consignee ? ' user-address-form__input--error' : ''}`}
                    placeholder='Recipient name'
                    {...register('consignee')}
                />
                {errors.consignee && <span className='user-address-form__error'>{errors.consignee.message}</span>}
            </div>

            <div className='user-address-form__field'>
                <label className='user-address-form__label'>Address Label <span className='user-address-form__required'>*</span></label>
                <input
                    type='text'
                    className={`user-address-form__input${errors.addressLabel ? ' user-address-form__input--error' : ''}`}
                    placeholder='e.g. Home, Office, etc.'
                    {...register('addressLabel')}
                />
                {errors.addressLabel && <span className='user-address-form__error'>{errors.addressLabel.message}</span>}
            </div>

            <div className='user-address-form__field'>
                <label className='user-address-form__label'>Address <span className='user-address-form__required'>*</span></label>
                <input
                    type='text'
                    className={`user-address-form__input${errors.address ? ' user-address-form__input--error' : ''}`}
                    placeholder='Enter your full address'
                    {...register('address')}
                />
                {errors.address && <span className='user-address-form__error'>{errors.address.message}</span>}
            </div>

            <div className='user-address-form__field'>
                <label className='user-address-form__label'>Postal Code <span className='user-address-form__required'>*</span></label>
                <input
                    type='text'
                    className={`user-address-form__input${errors.postalCode ? ' user-address-form__input--error' : ''}`}
                    placeholder='Enter postal code'
                    {...register('postalCode')}
                />
                {errors.postalCode && <span className='user-address-form__error'>{errors.postalCode.message}</span>}
            </div>

            <div className='user-address-form__field'>
                <label className='user-address-form__label'>Country <span className='user-address-form__required'>*</span></label>
                <div className='user-address-form__select-wrapper'>
                    <select
                        className='user-address-form__input user-address-form__select'
                        {...register('country')}
                    >
                        <option value="Singapore">Singapore</option>
                    </select>
                </div>
            </div>

            <div className='user-address-form__field'>
                <label className='user-address-form__label'>Phone Number <span className='user-address-form__required'>*</span></label>
                <input
                    type='tel'
                    className={`user-address-form__input${errors.phone ? ' user-address-form__input--error' : ''}`}
                    placeholder='Enter phone number'
                    {...register('phone')}
                />
                {errors.phone && <span className='user-address-form__error'>{errors.phone.message}</span>}
            </div>

            {showIsDefault && (
            <div className='user-address-form__checkbox-group'>
                <label className='user-address-form__checkbox-label'>
                    <input
                        type='checkbox'
                        className='user-address-form__checkbox'
                        {...register('isDefault')}
                    />
                    <span className='user-address-form__checkbox-custom' />
                    <span className='user-address-form__checkbox-text'>
                        Set as default address
                    </span>
                    <small style={{ color: '#6c757d', marginTop: '4px', display: 'block', width: '100%' }}>
                        (automatically set as default for first address)
                    </small>
                </label>
            </div>
            )}
        </div>
    )
})

UserAddressForm.displayName = 'UserAddressForm'

export default UserAddressForm
